import React, { useEffect, useRef, useState } from "react";
import { IMDSSessionProps, SessionStatus } from "./session.types";
import {
  StyledSessionWrapper,
  StyledSessionTitle,
  StyledTitleAndStatusContainer,
  StyledTitleText,
  StyledSessionBody,
  StyledDebugContent,
  StyledDebugSessionId,
} from "./session.style";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import styled from "@emotion/styled";
import {
  MDSDotPulseLoader,
  MDSDotPulseLoaderKind,
  MDSDotPulseLoaderSize,
} from "@/mds/components/dot-pulse-loader";
import { motion, AnimatePresence } from "motion/react";
import { VirtuosoMessageListLicense, VirtuosoMessageList } from "@virtuoso.dev/message-list";
import type { ItemLocation, VirtuosoMessageListMethods } from "@virtuoso.dev/message-list";
import {
  useSessionItems,
  SessionItem,
  SessionItemContext,
} from "./session-item/session-item.utils";
import { SessionItemRenderer } from "@/mds/components/sessions/session-item/session-item-renderer";
import { MDSSessionProvider, useMDSSession } from "./context";
import { SessionArtifactViewer } from "./session-parts/artifacts/viewer";

// Internal component that uses the context
const MDSSessionContent: React.FC<IMDSSessionProps> = ({
  sessionId,
  title,
  status,
  events,
  debugModeEnabled,
  containerHeight = 400,
}) => {
  const messagesRef = useRef<VirtuosoMessageListMethods<SessionItem, SessionItemContext>>(null);
  const [hasInitiallyRendered, setHasInitiallyRendered] = useState(false);
  const { state } = useMDSSession();

  // Process events into session items using the reusable hook
  const sessionItems = useSessionItems(events);

  // Update the virtualized message list when session items change
  // This handles both initial render and subsequent updates with different behaviors:
  // - Initial render: instant positioning with item size cache purging for accurate sizing
  // - Subsequent updates: smooth scrolling animation
  // Note that virtuoso has dedicated "initialLocation" prop that does only part of the job unfortunately
  // The bug was:
  // - render component in deeper hierarchy (like chrome ext). it renders, all good
  // - unmount parent component and mount it back on
  useEffect(() => {
    if (messagesRef.current && sessionItems.length > 0) {
      const isInitialRender = !hasInitiallyRendered;

      const bottomOffset = messagesRef?.current?.getScrollLocation().bottomOffset || 0;
      const initialLocation: ItemLocation = {
        index: "LAST",
        align: "end",
        behavior: customSmoothScroll,
      };

      messagesRef.current.data.replace(sessionItems, {
        initialLocation: bottomOffset < 100 ? initialLocation : undefined,
      });

      if (isInitialRender) {
        setHasInitiallyRendered(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionItems]);

  return (
    <StyledSessionWrapper>
      {/* Session Title */}
      <StyledSessionTitle>
        {title && (
          <StyledTitleAndStatusContainer>
            <StyledTitleText>{title}</StyledTitleText>
          </StyledTitleAndStatusContainer>
        )}
      </StyledSessionTitle>

      {/* Session Body */}
      <StyledSessionBody>
        {/* Artifact Viewer Pane */}
        <SessionArtifactViewer isVisible={!!state.activeArtifact} />

        {/* Session Messages */}
        <MessagesContainer>
          <VirtuosoMessageListLicense licenseKey={import.meta.env.VITE_VIRTUOSO_KEY}>
            <VirtuosoMessageList
              ref={messagesRef}
              ItemContent={SessionItemRenderer}
              context={{ status }}
              initialData={sessionItems}
              computeItemKey={params => (params.data as SessionItem).id}
              style={{
                height: containerHeight,
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                overflowX: "hidden",
                overflowY: "auto",
                overscrollBehaviorY: "contain",
                paddingLeft: 16,
                paddingRight: 16,
              }}
              shortSizeAlign="bottom"
            />
          </VirtuosoMessageListLicense>
          <AnimatePresence>
            {status === SessionStatus.Processing && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <LoaderWrapper>
                  <MDSDotPulseLoader
                    size={MDSDotPulseLoaderSize.Small}
                    kind={MDSDotPulseLoaderKind.Brand}
                  />
                </LoaderWrapper>
              </motion.div>
            )}
          </AnimatePresence>
        </MessagesContainer>
      </StyledSessionBody>

      {debugModeEnabled && (
        <StyledDebugContent
          onClick={() => {
            navigator.clipboard.writeText(sessionId);
          }}
        >
          <StyledDebugSessionId>
            <MdsIcon kind={MdsIconKind.Copy} />
            {sessionId}
          </StyledDebugSessionId>
        </StyledDebugContent>
      )}
    </StyledSessionWrapper>
  );
};

// Main component that provides the context
export const MDSSession: React.FC<IMDSSessionProps> = props => {
  return (
    <MDSSessionProvider>
      <MDSSessionContent {...props} />
    </MDSSessionProvider>
  );
};

const MessagesContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;

  /* Hide scrollbars */
  & [data-virtuoso-scroller] {
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */

    &::-webkit-scrollbar {
      display: none; /* Chrome, Safari, Opera */
    }
  }

  /* Fade effect at top and bottom */
  &::before,
  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    pointer-events: none;
    z-index: 1;
  }

  &::before {
    top: 0;
    height: 24px;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0));
  }

  &::after {
    bottom: 0;
    height: 8px;
    background: linear-gradient(to top, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0));
  }
`;

const LoaderWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 12px;
  margin-left: 30px;
  margin-top: 4px;
  margin-bottom: 4px;
`;

const customSmoothScroll = () => {
  const easeOutQuad = (x: number): number => 1 - (1 - x) * (1 - x);

  return {
    // increase the animation frame count to smoothen and slow down the scroll.
    animationFrameCount: 10,
    easing: easeOutQuad,
  };
};
